import type { PixelData, YUVDataLayout } from "@/core/utils/image-data"
import type { ChunkedJPEGBytes } from "./jpeg-chunks"

const JPEG_MCU_SIZE = 16
const MAX_RESTART_INTERVAL = 0xffff

export type YUVPixelData = PixelData & {
	data: Uint8Array
	yuvDataLayout: YUVDataLayout
}

export interface YUVBandDescriptor {
	index: number
	startY: number
	height: number
	mcuRows: number
}

export interface YUVBandSchedule {
	width: number
	height: number
	workerCount: number
	mcusPerRow: number
	mcuRowsPerBand: number
	restartInterval: number
	bands: YUVBandDescriptor[]
}

export interface YUVBand extends YUVBandDescriptor {
	pixelData: YUVPixelData
}

export interface PreparedYUVBands extends Omit<YUVBandSchedule, "bands"> {
	bands: YUVBand[]
}

export interface ParsedJpeg {
	width: number
	height: number
	sofMarkerOffset: number
	sofHeightOffset: number
	scanMarkerOffset: number
	entropyOffset: number
	entropyEnd: number
	driMarkerOffset: number | null
}

export type EncodedJPEGBand = Uint8Array | ChunkedJPEGBytes

interface JPEGByteSpan {
	bytes: Uint8Array
	start: number
	end: number
}

class JPEGByteSequence {
	public readonly byteLength: number
	private readonly spans: JPEGByteSpan[] = []

	public constructor(source: EncodedJPEGBand) {
		const chunks = source instanceof Uint8Array ? [source] : source.chunks
		const byteLength = source.byteLength

		if (!Number.isSafeInteger(byteLength) || byteLength < 0) {
			throw new Error("JPEG byte source has an invalid byte length")
		}

		this.byteLength = byteLength
		let representedByteLength = 0

		for (const chunk of chunks) {
			if (!(chunk instanceof Uint8Array)) {
				throw new Error("JPEG byte source contains an invalid chunk")
			}
			if (representedByteLength === byteLength) {
				break
			}

			const usedByteLength = Math.min(chunk.byteLength, byteLength - representedByteLength)
			if (usedByteLength === 0) {
				continue
			}

			const bytes =
				usedByteLength === chunk.byteLength ? chunk : chunk.subarray(0, usedByteLength)
			this.spans.push({
				bytes,
				start: representedByteLength,
				end: representedByteLength + usedByteLength,
			})
			representedByteLength += usedByteLength
		}

		if (representedByteLength !== byteLength) {
			throw new Error("JPEG byte source is smaller than its reported byte length")
		}
	}

	public get(offset: number): number | undefined {
		const spanIndex = this.findSpanIndex(offset)
		if (spanIndex < 0) {
			return undefined
		}

		const span = this.spans[spanIndex]
		return span?.bytes[offset - span.start]
	}

	public indexOf(value: number, fromIndex: number): number {
		if (this.byteLength === 0 || fromIndex >= this.byteLength) {
			return -1
		}

		const normalizedFromIndex = Math.max(0, fromIndex)
		const firstSpanIndex = this.findSpanIndex(normalizedFromIndex)
		if (firstSpanIndex < 0) {
			return -1
		}

		for (let spanIndex = firstSpanIndex; spanIndex < this.spans.length; spanIndex++) {
			const span = this.spans[spanIndex]
			if (!span) {
				continue
			}

			const localFromIndex = Math.max(0, normalizedFromIndex - span.start)
			const localIndex = span.bytes.indexOf(value, localFromIndex)
			if (localIndex >= 0) {
				return span.start + localIndex
			}
		}

		return -1
	}

	public copyTo(target: Uint8Array, targetOffset: number, start: number, end: number): number {
		if (
			!Number.isSafeInteger(start) ||
			!Number.isSafeInteger(end) ||
			start < 0 ||
			end < start ||
			end > this.byteLength
		) {
			throw new Error("Invalid JPEG byte source copy range")
		}

		const copyByteLength = end - start
		if (
			!Number.isSafeInteger(targetOffset) ||
			targetOffset < 0 ||
			targetOffset + copyByteLength > target.byteLength
		) {
			throw new Error("JPEG byte source copy exceeds its target")
		}
		if (copyByteLength === 0) {
			return 0
		}

		let spanIndex = this.findSpanIndex(start)
		let sourceOffset = start
		let outputOffset = targetOffset

		while (sourceOffset < end && spanIndex >= 0 && spanIndex < this.spans.length) {
			const span = this.spans[spanIndex]
			if (!span) {
				break
			}

			const spanCopyStart = Math.max(sourceOffset, span.start)
			const spanCopyEnd = Math.min(end, span.end)
			const localStart = spanCopyStart - span.start
			const localEnd = spanCopyEnd - span.start
			const bytes =
				localStart === 0 && localEnd === span.bytes.byteLength
					? span.bytes
					: span.bytes.subarray(localStart, localEnd)

			target.set(bytes, outputOffset)
			outputOffset += bytes.byteLength
			sourceOffset = spanCopyEnd
			spanIndex++
		}

		if (sourceOffset !== end) {
			throw new Error("JPEG byte source ended during copy")
		}

		return copyByteLength
	}

	private findSpanIndex(offset: number): number {
		if (!Number.isSafeInteger(offset) || offset < 0 || offset >= this.byteLength) {
			return -1
		}

		let low = 0
		let high = this.spans.length - 1

		while (low <= high) {
			const middle = (low + high) >> 1
			const span = this.spans[middle]
			if (!span) {
				return -1
			}
			if (offset < span.start) {
				high = middle - 1
			} else if (offset >= span.end) {
				low = middle + 1
			} else {
				return middle
			}
		}

		return -1
	}
}

export function getYUVBandSchedule(
	width: number,
	height: number,
	workerCount: number,
): YUVBandSchedule {
	assertPositiveInteger(width, "JPEG width")
	assertPositiveInteger(height, "JPEG height")
	assertPositiveInteger(workerCount, "JPEG worker count")

	if (width > MAX_RESTART_INTERVAL || height > MAX_RESTART_INTERVAL) {
		throw new Error("JPEG dimensions must fit in the 16-bit SOF fields")
	}

	const mcusPerRow = Math.ceil(width / JPEG_MCU_SIZE)
	const totalMcuRows = Math.ceil(height / JPEG_MCU_SIZE)
	const maxMcuRowsPerBand = Math.floor(MAX_RESTART_INTERVAL / mcusPerRow)

	if (maxMcuRowsPerBand < 1) {
		throw new Error("JPEG row is too wide for a 16-bit restart interval")
	}

	const targetMcuRowsPerBand = Math.ceil(totalMcuRows / workerCount)
	const mcuRowsPerBand = Math.min(targetMcuRowsPerBand, maxMcuRowsPerBand)
	const restartInterval = mcusPerRow * mcuRowsPerBand
	const bandPixelHeight = mcuRowsPerBand * JPEG_MCU_SIZE
	const bands: YUVBandDescriptor[] = []

	for (let startY = 0, index = 0; startY < height; startY += bandPixelHeight, index++) {
		const bandHeight = Math.min(bandPixelHeight, height - startY)
		bands.push({
			index,
			startY,
			height: bandHeight,
			mcuRows: Math.ceil(bandHeight / JPEG_MCU_SIZE),
		})
	}

	return {
		width,
		height,
		workerCount,
		mcusPerRow,
		mcuRowsPerBand,
		restartInterval,
		bands,
	}
}

export function prepareYUVBands(
	pixelData: PixelData,
	workerCount: number,
	copyPlanes: boolean,
): PreparedYUVBands {
	const source = asYUVPixelData(pixelData)
	const schedule = getYUVBandSchedule(source.width, source.height, workerCount)
	const chromaWidth = Math.ceil(source.width / 2)
	const sourceLayout = source.yuvDataLayout
	const bands = schedule.bands.map((descriptor): YUVBand => {
		const chromaStartY = descriptor.startY / 2
		const chromaHeight = Math.ceil(descriptor.height / 2)
		const yLength = source.width * descriptor.height
		const chromaLength = chromaWidth * chromaHeight
		let data: Uint8Array
		let yuvDataLayout: YUVDataLayout

		if (copyPlanes) {
			data = new Uint8Array(yLength + chromaLength * 2)
			copyPlaneRows({
				source: source.data,
				sourceOffset: sourceLayout.yOffset,
				sourceStride: source.width,
				startRow: descriptor.startY,
				rowCount: descriptor.height,
				target: data,
				targetOffset: 0,
			})
			copyPlaneRows({
				source: source.data,
				sourceOffset: sourceLayout.uOffset,
				sourceStride: chromaWidth,
				startRow: chromaStartY,
				rowCount: chromaHeight,
				target: data,
				targetOffset: yLength,
			})
			copyPlaneRows({
				source: source.data,
				sourceOffset: sourceLayout.vOffset,
				sourceStride: chromaWidth,
				startRow: chromaStartY,
				rowCount: chromaHeight,
				target: data,
				targetOffset: yLength + chromaLength,
			})
			yuvDataLayout = {
				yOffset: 0,
				yLength,
				uOffset: yLength,
				uLength: chromaLength,
				vOffset: yLength + chromaLength,
				vLength: chromaLength,
			}
		} else {
			data = source.data
			yuvDataLayout = {
				yOffset: sourceLayout.yOffset + descriptor.startY * source.width,
				yLength,
				uOffset: sourceLayout.uOffset + chromaStartY * chromaWidth,
				uLength: chromaLength,
				vOffset: sourceLayout.vOffset + chromaStartY * chromaWidth,
				vLength: chromaLength,
			}
		}

		return {
			...descriptor,
			pixelData: {
				data,
				yuvDataLayout,
				width: source.width,
				height: descriptor.height,
				// Band JPEGs must not receive per-band ICC/EXIF. Metadata is applied once after assembly.
				colorSpace: "srgb",
			},
		}
	})

	return {
		...schedule,
		bands,
	}
}

export function assembleRestartedJpeg(
	encodedBands: readonly EncodedJPEGBand[],
	options: {
		width: number
		height: number
		restartInterval: number
	},
): Uint8Array {
	if (encodedBands.length === 0) {
		throw new Error("Parallel JPEG assembly requires at least one encoded band")
	}

	assertPositiveInteger(options.width, "JPEG width")
	assertPositiveInteger(options.height, "JPEG height")
	assertPositiveInteger(options.restartInterval, "JPEG restart interval")

	if (options.restartInterval > MAX_RESTART_INTERVAL) {
		throw new Error("JPEG restart interval exceeds its 16-bit field")
	}

	const bandBytes = encodedBands.map((band) => new JPEGByteSequence(band))
	const parsedBands = bandBytes.map(parseJpegBytes)
	const firstBytes = bandBytes[0]
	const first = parsedBands[0]

	if (!firstBytes || !first) {
		throw new Error("Parallel JPEG assembly could not read the first band")
	}

	let summedHeight = 0

	for (let index = 0; index < parsedBands.length; index++) {
		const parsed = parsedBands[index]
		const bytes = bandBytes[index]

		if (!parsed || !bytes) {
			throw new Error(`Parallel JPEG band ${index + 1} is missing`)
		}
		if (parsed.width !== options.width) {
			throw new Error(
				`Parallel JPEG band ${index + 1} has width ${parsed.width}, expected ${options.width}`,
			)
		}
		if (parsed.driMarkerOffset !== null) {
			throw new Error(`Parallel JPEG band ${index + 1} already contains a DRI marker`)
		}
		if (index > 0 && !haveEqualNormalizedHeaders(firstBytes, first, bytes, parsed)) {
			throw new Error(`Parallel JPEG band ${index + 1} uses a different JPEG header`)
		}

		summedHeight += parsed.height
	}

	if (summedHeight !== options.height) {
		throw new Error(
			`Parallel JPEG bands total ${summedHeight} rows, expected ${options.height}`,
		)
	}

	const driByteLength = 6
	const headerByteLength = first.entropyOffset + driByteLength
	const entropyByteLength = parsedBands.reduce(
		(total, parsed) => total + parsed.entropyEnd - parsed.entropyOffset,
		0,
	)
	const output = new Uint8Array(
		headerByteLength + entropyByteLength + (encodedBands.length - 1) * 2 + 2,
	)
	let outputOffset = firstBytes.copyTo(output, 0, 0, first.scanMarkerOffset)
	output[outputOffset++] = 0xff
	output[outputOffset++] = 0xdd
	output[outputOffset++] = 0x00
	output[outputOffset++] = 0x04
	output[outputOffset++] = (options.restartInterval >> 8) & 0xff
	output[outputOffset++] = options.restartInterval & 0xff
	outputOffset += firstBytes.copyTo(
		output,
		outputOffset,
		first.scanMarkerOffset,
		first.entropyOffset,
	)
	output[first.sofHeightOffset] = (options.height >> 8) & 0xff
	output[first.sofHeightOffset + 1] = options.height & 0xff

	if (outputOffset !== headerByteLength) {
		throw new Error("Parallel JPEG assembly produced an unexpected header byte length")
	}

	for (let index = 0; index < encodedBands.length; index++) {
		const bytes = bandBytes[index]
		const parsed = parsedBands[index]
		if (!bytes || !parsed) {
			throw new Error(`Parallel JPEG band ${index + 1} is missing during assembly`)
		}

		outputOffset += bytes.copyTo(output, outputOffset, parsed.entropyOffset, parsed.entropyEnd)

		if (index < encodedBands.length - 1) {
			output[outputOffset++] = 0xff
			output[outputOffset++] = 0xd0 + (index % 8)
		}
	}

	output[outputOffset++] = 0xff
	output[outputOffset++] = 0xd9

	if (outputOffset !== output.byteLength) {
		throw new Error("Parallel JPEG assembly produced an unexpected byte length")
	}

	return output
}

export function parseJpeg(bytes: Uint8Array): ParsedJpeg {
	return parseJpegBytes(new JPEGByteSequence(bytes))
}

function parseJpegBytes(bytes: JPEGByteSequence): ParsedJpeg {
	if (bytes.byteLength < 4 || bytes.get(0) !== 0xff || bytes.get(1) !== 0xd8) {
		throw new Error("Invalid JPEG SOI marker")
	}

	let offset = 2
	let sofMarkerOffset = -1
	let sofHeightOffset = -1
	let width = 0
	let height = 0
	let driMarkerOffset: number | null = null

	while (offset < bytes.byteLength) {
		if (bytes.get(offset) !== 0xff) {
			throw new Error(`Invalid JPEG marker at byte ${offset}`)
		}

		const markerOffset = offset
		while (bytes.get(offset) === 0xff) {
			offset++
		}

		const marker = bytes.get(offset)
		offset++
		if (marker === undefined) {
			throw new Error("Truncated JPEG marker")
		}
		if (marker === 0xd9) {
			throw new Error("JPEG ended before its scan")
		}
		if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) {
			continue
		}
		if (offset + 1 >= bytes.byteLength) {
			throw new Error("Truncated JPEG segment length")
		}

		const segmentLength = readUint16(bytes, offset)
		if (segmentLength < 2 || offset + segmentLength > bytes.byteLength) {
			throw new Error(`Invalid JPEG segment length at byte ${markerOffset}`)
		}

		if (isStartOfFrame(marker)) {
			if (segmentLength < 8) {
				throw new Error("Invalid JPEG SOF segment")
			}
			sofMarkerOffset = markerOffset
			sofHeightOffset = offset + 3
			height = readUint16(bytes, sofHeightOffset)
			width = readUint16(bytes, sofHeightOffset + 2)
		}

		if (marker === 0xdd) {
			driMarkerOffset = markerOffset
		}

		if (marker === 0xda) {
			if (sofMarkerOffset < 0) {
				throw new Error("JPEG scan appears before its SOF segment")
			}

			return {
				width,
				height,
				sofMarkerOffset,
				sofHeightOffset,
				scanMarkerOffset: markerOffset,
				entropyOffset: offset + segmentLength,
				entropyEnd: findEntropyEnd(bytes, offset + segmentLength),
				driMarkerOffset,
			}
		}

		offset += segmentLength
	}

	throw new Error("JPEG has no SOS segment")
}

function asYUVPixelData(pixelData: PixelData): YUVPixelData {
	if (!(pixelData.data instanceof Uint8Array) || !pixelData.yuvDataLayout) {
		throw new Error("Parallel JPEG encoding requires 8-bit planar YUV pixel data")
	}

	const layout = pixelData.yuvDataLayout
	const chromaWidth = Math.ceil(pixelData.width / 2)
	const chromaHeight = Math.ceil(pixelData.height / 2)
	const expectedYLength = pixelData.width * pixelData.height
	const expectedChromaLength = chromaWidth * chromaHeight

	if (
		layout.yLength < expectedYLength ||
		layout.uLength < expectedChromaLength ||
		layout.vLength < expectedChromaLength ||
		layout.yOffset + expectedYLength > pixelData.data.byteLength ||
		layout.uOffset + expectedChromaLength > pixelData.data.byteLength ||
		layout.vOffset + expectedChromaLength > pixelData.data.byteLength
	) {
		throw new Error("YUV plane layout is smaller than the declared image dimensions")
	}

	return {
		...pixelData,
		data: pixelData.data,
		yuvDataLayout: pixelData.yuvDataLayout,
	}
}

function copyPlaneRows(options: {
	source: Uint8Array
	sourceOffset: number
	sourceStride: number
	startRow: number
	rowCount: number
	target: Uint8Array
	targetOffset: number
}) {
	for (let row = 0; row < options.rowCount; row++) {
		const sourceStart = options.sourceOffset + (options.startRow + row) * options.sourceStride
		const targetStart = options.targetOffset + row * options.sourceStride
		options.target.set(
			options.source.subarray(sourceStart, sourceStart + options.sourceStride),
			targetStart,
		)
	}
}

function haveEqualNormalizedHeaders(
	leftBytes: JPEGByteSequence,
	left: ParsedJpeg,
	rightBytes: JPEGByteSequence,
	right: ParsedJpeg,
) {
	if (left.entropyOffset !== right.entropyOffset) {
		return false
	}

	for (let offset = 0; offset < left.entropyOffset; offset++) {
		const leftByte =
			offset === left.sofHeightOffset || offset === left.sofHeightOffset + 1
				? 0
				: leftBytes.get(offset)
		const rightByte =
			offset === right.sofHeightOffset || offset === right.sofHeightOffset + 1
				? 0
				: rightBytes.get(offset)

		if (leftByte !== rightByte) {
			return false
		}
	}
	return true
}

function findEntropyEnd(bytes: JPEGByteSequence, entropyOffset: number) {
	let offset = entropyOffset
	while (offset < bytes.byteLength) {
		const markerOffset = bytes.indexOf(0xff, offset)
		if (markerOffset < 0) {
			break
		}

		offset = markerOffset
		while (bytes.get(offset) === 0xff) {
			offset++
		}

		const marker = bytes.get(offset)
		if (marker === 0x00 || (marker !== undefined && marker >= 0xd0 && marker <= 0xd7)) {
			offset++
			continue
		}
		if (marker === 0xd9) {
			return markerOffset
		}
		if (marker === undefined) {
			break
		}
		throw new Error(`Unexpected JPEG marker 0x${marker.toString(16)} inside scan data`)
	}

	throw new Error("JPEG scan has no EOI marker")
}

function isStartOfFrame(marker: number) {
	return marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc
}

function readUint16(bytes: JPEGByteSequence, offset: number) {
	const high = bytes.get(offset)
	const low = bytes.get(offset + 1)
	if (high === undefined || low === undefined) {
		throw new Error("Truncated 16-bit JPEG field")
	}
	return (high << 8) | low
}

function assertPositiveInteger(value: number, label: string) {
	if (!Number.isInteger(value) || value <= 0) {
		throw new Error(`${label} must be a positive integer`)
	}
}
